// [목적] 뱃지 도감 표시 자원 — kind → 아이콘 path, tier → 메달 팔레트, category → 섹션 라벨.
// [이유] 2026-09-21 badge-catalog: 라벨·조건 문구는 BE(badge-definitions.ts)가 내려주지만 "어떻게 생겼는지"는
//        FE 자산이다. ProfilePage / ProfileMobile 이 같은 표를 각각 복사하지 않도록 한 곳에 모은다
//        (utils/badgeKind.ts 가 emoji 표를 통합한 것과 같은 이유 — 다만 이모지는 OS별 렌더 편차가 커서
//         라인 아이콘으로 교체하는 흐름을 따른다: components/icons/LineIcons.tsx 헤더 주석 참고).
// [사이드 이팩트] 없음(순수 상수/함수). 색은 packages/ui 토큰만 사용 — hex 직접 입력 금지(CLAUDE.md §6-3).
// [수정 시 고려사항] BE 가 새 kind 를 추가하면 BADGE_ICON_PATHS 에 24x24 스트로크 path 1건만 추가하면 된다
//                   (없으면 기본 메달 아이콘으로 폴백하므로 화면이 깨지지는 않는다).

import { tokens } from '@gangwoncanvas/ui';
import type { BadgeCatalogItem } from '@gangwoncanvas/types';

const c = tokens.color;
const L = 'light' as const;

/** 메달 1개를 그리는 데 필요한 색 4종 */
export interface BadgeMedalPalette {
  bgFrom: string;
  bgTo: string;
  fg: string;
  ring: string;
}

export const BADGE_TIER_PALETTE: Record<BadgeCatalogItem['tier'] | 'locked', BadgeMedalPalette> = {
  gold: {
    bgFrom: c.badge.gold.bgFrom[L],
    bgTo: c.badge.gold.bgTo[L],
    fg: c.badge.gold.fg[L],
    ring: c.badge.gold.ring[L],
  },
  silver: {
    bgFrom: c.badge.silver.bgFrom[L],
    bgTo: c.badge.silver.bgTo[L],
    fg: c.badge.silver.fg[L],
    ring: c.badge.silver.ring[L],
  },
  bronze: {
    bgFrom: c.badge.bronze.bgFrom[L],
    bgTo: c.badge.bronze.bgTo[L],
    fg: c.badge.bronze.fg[L],
    ring: c.badge.bronze.ring[L],
  },
  locked: {
    bgFrom: c.badge.locked.bgFrom[L],
    bgTo: c.badge.locked.bgTo[L],
    fg: c.badge.locked.fg[L],
    ring: c.badge.locked.ring[L],
  },
};

/** 등급 한글 라벨 — 상세 다이얼로그 칩 */
export const BADGE_TIER_LABEL: Record<BadgeCatalogItem['tier'], string> = {
  gold: '골드',
  silver: '실버',
  bronze: '브론즈',
};

/** 카테고리 한글 라벨 — 도감 섹션 헤더 */
export const BADGE_CATEGORY_LABEL: Record<BadgeCatalogItem['category'], string> = {
  start: '시작',
  social: '인기',
  theme: '취향',
  explore: '탐험',
};

/**
 * kind → 24x24 라인 아이콘 path.
 * LineIcons.tsx 와 동일 규격(viewBox 24, stroke 1.8, round cap)이라 시각 톤이 어긋나지 않는다.
 * path 좌표는 디자인 토큰 대상이 아닌 순수 지오메트리(LineIcons.tsx 헤더 주석의 예외 규정과 동일).
 */
export const BADGE_ICON_PATHS: Record<string, string> = {
  // 4각 별 — 첫걸음
  first_folio:
    'M12 3c.7 4.6 2.7 6.6 7.3 7.3-4.6.7-6.6 2.7-7.3 7.3-.7-4.6-2.7-6.6-7.3-7.3C9.3 9.6 11.3 7.6 12 3Z',
  // 로켓 — 얼리버드
  pioneer:
    'M12 3c2.9 2.1 4.6 5.3 4.6 8.7L13.6 14h-3.2l-3-2.3C7.4 8.3 9.1 5.1 12 3Zm0 4.6a1.6 1.6 0 1 0 .001 3.201A1.6 1.6 0 0 0 12 7.6ZM10.4 14.2 9.2 19l2.8-1.8 2.8 1.8-1.2-4.8M7.4 13.6 5 16M16.6 13.6 19 16',
  // 하트 — 좋아요
  first_100_likes:
    'M20.8 6.6a4.7 4.7 0 0 0-6.7 0L12 8.7l-2.1-2.1a4.7 4.7 0 1 0-6.7 6.7L12 21l8.8-9.7a4.7 4.7 0 0 0 0-4.7Z',
  // 분기 — Fork
  fork_master:
    'M6 4a2 2 0 1 0 .001 4.001A2 2 0 0 0 6 4Zm12 0a2 2 0 1 0 .001 4.001A2 2 0 0 0 18 4ZM6 16a2 2 0 1 0 .001 4.001A2 2 0 0 0 6 16ZM6 8v8M18 8v1.5a4.5 4.5 0 0 1-4.5 4.5H6',
  // 나침반 — 탐험
  gangwon_explorer: 'M12 3a9 9 0 1 0 .001 18.001A9 9 0 0 0 12 3Zm3.8 5.2-2.3 5.3-5.3 2.3 2.3-5.3Z',
  // 파도
  sea_master:
    'M3 8.5c1.8-1.4 3.6-1.4 5.4 0s3.6 1.4 5.4 0 3.6-1.4 5.4 0M3 13c1.8-1.4 3.6-1.4 5.4 0s3.6 1.4 5.4 0 3.6-1.4 5.4 0M3 17.5c1.8-1.4 3.6-1.4 5.4 0s3.6 1.4 5.4 0 3.6-1.4 5.4 0',
  // 커피잔
  cafe_tour:
    'M4.5 8h11v4.8a4.8 4.8 0 0 1-4.8 4.8H9.3a4.8 4.8 0 0 1-4.8-4.8Zm11 1.2H17a2.4 2.4 0 0 1 0 4.8h-1.5M7 3.4c0 1.1.9 1.1.9 2.2M11 3.4c0 1.1.9 1.1.9 2.2M3.5 20.4h13',
  // 텐트
  camping_lover: 'm12 4 8.5 15.5h-17L12 4Zm0 6.6-4.8 8.9M12 10.6l4.8 8.9M9.4 19.5 12 14.6l2.6 4.9',
  // 서핑보드 + 물결
  surf_rider:
    'M7.2 18.8C12.8 17.4 17.4 12.8 19 6.4 12.6 8 8 12.6 6.6 18.2M3 21c1.8-.7 3.2-1.7 4.2-3M15 21c1.8-.7 3.2-1.7 4.2-3',
  // 발바닥
  pet_friendly:
    'M8.4 6.6a1.8 2.3 0 1 0 .001 4.601A1.8 2.3 0 0 0 8.4 6.6Zm7.2 0a1.8 2.3 0 1 0 .001 4.601A1.8 2.3 0 0 0 15.6 6.6ZM4.6 12a1.6 2 0 1 0 .001 4.001A1.6 2 0 0 0 4.6 12Zm14.8 0a1.6 2 0 1 0 .001 4.001A1.6 2 0 0 0 19.4 12ZM12 12.8c2.3 0 4.2 1.9 4.2 3.8 0 1.7-1.4 2.8-3.1 2.8h-2.2c-1.7 0-3.1-1.1-3.1-2.8 0-1.9 1.9-3.8 4.2-3.8Z',
};

/** 아이콘이 없는 kind 폴백 — 리본 달린 메달 */
export const BADGE_FALLBACK_ICON_PATH =
  'M12 3a5.5 5.5 0 1 0 .001 11.001A5.5 5.5 0 0 0 12 3Zm-3.4 9.6L7 21l5-2.6L17 21l-1.6-8.4';

/** 자물쇠 — 미획득 메달 위 오버레이 */
export const BADGE_LOCK_ICON_PATH =
  'M6 11h12a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Zm2 0V7a4 4 0 0 1 8 0v4';

export function getBadgeIconPath(kind: string): string {
  return BADGE_ICON_PATHS[kind] ?? BADGE_FALLBACK_ICON_PATH;
}

/**
 * [목적] 진행률 문구 — "3/5곳" · "97/100개"
 * [이유] 미획득 배지에서 "얼마나 남았는지"가 도감의 핵심 동기 부여 장치다.
 * [수정 시 고려사항] target 이 1(불리언형 배지: first_folio/pioneer)이면 수치 표기가 의미 없어
 *                   호출부가 이 문구 대신 조건 설명만 보여준다.
 */
export function formatBadgeProgress(progress: BadgeCatalogItem['progress']): string {
  return `${progress.current.toLocaleString('ko-KR')}/${progress.target.toLocaleString('ko-KR')}${progress.unit}`;
}

/** 진행률 0~1 (target 0 방어) */
export function badgeProgressRatio(progress: BadgeCatalogItem['progress']): number {
  if (progress.target <= 0) return 0;
  return Math.min(1, Math.max(0, progress.current / progress.target));
}

/** awardedAt(ISO) → "2026.09.20" */
export function formatBadgeAwardedAt(awardedAt: string | null): string {
  if (!awardedAt) return '';
  const d = new Date(awardedAt);
  if (Number.isNaN(d.getTime())) return awardedAt.slice(0, 10);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}.${mm}.${dd}`;
}
